/**
 * Notification Types Configuration (ESM version for the frontend)
 *
 * Keep in sync with functions/notification-types.js (CommonJS) — identical
 * object shape, only the export form differs.
 *
 * Properties:
 * - title: default title for the notification
 * - roles: array of roles that receive this notification via notifyByType
 * - targeted: true if sent to specific employees (e.g., assigned salespeople)
 * - customer: true if sent to customers
 * - sendEmail: default email preference for this notification type
 * - detailedEmail: true if this notification type sends a detailed email with attachments
 */

export default {
  ackReview: {
    category: "sales",
    icon: "review_pending",
    title: "Awaiting Review",
    roles: [],
    targeted: true, // assigned salespeople
    sendEmail: true,
  },
  balanceDue: {
    category: "accounting",
    icon: "cash",
    title: "Balance Due Reminder",
    roles: [],
    customer: true,
    sendEmail: true,
  },
  collectDeliveryBalance: {
    category: "accounting",
    icon: "cash",
    title: "Collect Delivery Balance",
    roles: ["accounting"],
    sendEmail: false,
  },
  collectDownPayment: {
    category: "accounting",
    icon: "cash",
    title: "Collect Down Payment",
    roles: ["accounting"],
    sendEmail: false,
  },
  collectMaterial: {
    category: "accounting",
    icon: "cash",
    title: "Collect Material Balance",
    roles: ["accounting", "purchaser"],
    sendEmail: false,
  },
  // Accounting approved the company account the recipient asked for
  companyApproved: {
    category: "sales",
    icon: "company",
    title: "Company Account Approved",
    roles: [],
    targeted: true, // the requester
    sendEmail: false,
  },
  // A salesperson asked for a customer company account (Send Quote)
  companyRequested: {
    category: "accounting",
    icon: "company",
    title: "Company Account Requested",
    roles: ["accounting", "admin"],
    sendEmail: false,
  },
  criticalIssue: {
    category: "sales",
    icon: "issue",
    title: "Critical Sale Issue",
    roles: ["admin"],
    targeted: true, // assigned salespeople
    sendEmail: true,
  },
  libraryBulkDelete: {
    category: "admin",
    icon: "delete",
    title: "Library: Bulk Deletion Detected",
    roles: ["admin"],
    sendEmail: true,
  },
  newIssue: {
    category: "sales",
    icon: "issue",
    title: "New Sale Issue",
    roles: [],
    targeted: true, // assigned salespeople
    sendEmail: false,
  },
  receivedDownPayment: {
    category: "sales",
    icon: "paid",
    title: "Down Payment Received",
    roles: [],
    targeted: true, // assigned salespeople
    sendEmail: false,
  },
  newInvoice: {
    category: "accounting",
    icon: "invoice",
    title: "New Invoice",
    roles: ["accounting"],
    sendEmail: false,
    clientEmail: true,
  },
  // Deliberately no `clientEmail`: unlike newInvoice, a credit memo has never
  // reached the customer, because this type was missing from the config
  // entirely and notifyByType bailed out. Adding it here restores the
  // accounting notification without silently starting customer emails.
  newCreditMemo: {
    category: "accounting",
    icon: "invoice",
    title: "New Credit Memo",
    roles: ["accounting"],
    sendEmail: false,
  },
  invoiceAutoUpdated: {
    category: "accounting",
    icon: "invoice",
    title: "Invoice Updated",
    roles: ["accounting"],
    sendEmail: false,
  },
  voidInvoice: {
    category: "accounting",
    icon: "invoice",
    title: "Void Invoice",
    roles: ["accounting"],
    sendEmail: false,
  },
  unvoidInvoice: {
    category: "accounting",
    icon: "invoice",
    title: "Un-void Invoice",
    roles: ["accounting"],
    sendEmail: false,
  },
  deliveryComplete: {
    category: "delivery",
    icon: "delivered",
    title: "Delivery Complete",
    roles: ["accounting", "scheduling", "warehouse"],
    targeted: true, // assigned salespeople
    customer: true,
    sendEmail: true,
    detailedEmail: true,
  },
  pickupReady: {
    category: "delivery",
    icon: "pickup",
    title: "Ready for Pickup",
    roles: ["accounting", "scheduling", "warehouse"],
    targeted: true, // assigned salespeople
    customer: true,
    sendEmail: true,
  },
  pickupComplete: {
    category: "delivery",
    icon: "pickup",
    title: "Pickup Complete",
    roles: ["accounting", "scheduling", "warehouse"],
    targeted: true, // assigned salespeople
    customer: true,
    sendEmail: true,
  },
  deliveryCash: {
    category: "accounting",
    icon: "delivery",
    title: "New Cash Sale Delivery",
    roles: ["accounting", "purchaser"],
    sendEmail: false,
  },
  eventReschedule: {
    category: "scheduling",
    icon: "reschedule",
    title: "Reschedule",
    roles: ["scheduling"],
    targeted: true, // event creator
    sendEmail: false,
  },
  expenseSubmitted: {
    category: "accounting",
    icon: "expense",
    title: "Expense Submitted",
    roles: ["accounting"],
    sendEmail: false,
  },
  loadingStarted: {
    category: "delivery",
    icon: "loading",
    title: "Loading Started",
    roles: [],
    customer: true,
    sendEmail: true,
  },
  loadingComplete: {
    category: "delivery",
    icon: "loaded",
    title: "Loading Complete",
    roles: [],
    customer: true,
    sendEmail: true,
  },
  installationReport: {
    category: "installation",
    icon: "installation",
    title: "Installation Report",
    roles: ["accounting", "scheduling"],
    targeted: true, // assigned salespeople
    customer: true,
    sendEmail: true,
    detailedEmail: true,
  },
  installationStarted: {
    category: "installation",
    icon: "installation",
    title: "Installation Started",
    roles: ["installation"],
    customer: true,
    sendEmail: true,
  },
  inTransit: {
    category: "delivery",
    icon: "delivering",
    title: "In Transit",
    roles: [],
    customer: true,
    sendEmail: true,
  },
  mailSuccess: {
    category: "messaging",
    icon: "email_success",
    title: "Successfully sent email",
    roles: [],
    targeted: true, // email creator
    sendEmail: false,
  },
  mailError: {
    category: "messaging",
    icon: "email_fail",
    title: "Failed to send email",
    roles: [],
    targeted: true, // email creator
    sendEmail: false,
  },
  message: {
    category: "messaging",
    icon: "message",
    title: "New Message",
    roles: [],
    targeted: true, // message recipients
    sendEmail: false,
  },
  orderPlaced: {
    category: "sales",
    icon: "order",
    title: "Order Placed",
    roles: [],
    targeted: true,
    sendEmail: false,
  },
  payrollReminder: {
    category: "paychecks",
    icon: "cheque",
    title: "Payroll Reminder",
    roles: [],
    targeted: true,
    sendEmail: true,
  },
  paycheckApproved: {
    category: "paychecks",
    icon: "cheque",
    title: "Paycheck Approved",
    roles: [],
    targeted: true,
    sendEmail: false,
  },
  paycheckOverride: {
    category: "paychecks",
    icon: "cheque",
    title: "Paycheck Override",
    roles: [],
    targeted: true,
    sendEmail: false,
  },
  paycheckOverrideConfirmed: {
    category: "paychecks",
    icon: "cheque",
    title: "Paycheck Override Confirmed",
    roles: [],
    targeted: true,
    sendEmail: false,
  },
  paycheckNegativePaid: {
    category: "paychecks",
    icon: "cheque",
    title: "Negative Paycheck Marked Paid",
    roles: ["accounting", "admin", "management"],
    sendEmail: false,
  },
  paymentReceived: {
    category: "accounting",
    icon: "transaction",
    title: "Payment Received",
    roles: ["accounting"],
    sendEmail: false,
  },
  qcInspected: {
    category: "scheduling",
    icon: "warehouse",
    title: "Material Received",
    roles: ["scheduling"],
    targeted: true, // assigned salespeople
    sendEmail: false,
  },
  quoteResponse: {
    category: "quotes",
    icon: "quote",
    title: "New Quote Response",
    roles: [],
    targeted: true, // assigned salesperson
    sendEmail: false,
  },
  readyToOrder: {
    category: "sales",
    icon: "order",
    title: "Ready to Order",
    // Exclusive (touchSale): the purchasers when the sale defers ordering to
    // them, otherwise the assigned salespeople -- never both.
    roles: ["purchaser"],
    targeted: true, // assigned salespeople
    sendEmail: false,
  },
  saleCanceled: {
    category: "sales",
    icon: "cancel",
    title: "Sale Canceled",
    roles: ["admin", "accounting"],
    sendEmail: false,
  },
  salesLeadDiscarded: {
    category: "salesLeads",
    icon: "delete",
    title: "Sales Lead Discarded",
    roles: ["admin"],
    sendEmail: false,
  },
  salesLeadLost: {
    category: "salesLeads",
    icon: "lost",
    title: "Sales Lead Lost",
    roles: ["admin"],
    sendEmail: false,
  },
  salesLeadReferral: {
    category: "salesLeads",
    icon: "referred",
    title: "Follow Up",
    roles: [],
    targeted: true, // modifier (follow-up reminder)
    sendEmail: false,
  },
  sampleReadyForPickup: {
    category: "sales",
    icon: "sample",
    title: "Sample Ready for Pickup",
    roles: [],
    targeted: true, // the employee who dropped the sample off
    sendEmail: false,
  },
  submitPayApp: {
    category: "sales",
    icon: "invoice",
    title: "Submit Pay App",
    roles: [],
    targeted: true, // assigned salespeople
    sendEmail: false,
  },
  taskAssigned: {
    category: "messaging",
    icon: "task",
    title: "Task Assigned",
    roles: [],
    targeted: true, // assigned employee
    sendEmail: false,
  },
  taskCompleted: {
    category: "messaging",
    icon: "task_done",
    title: "Task Completed",
    roles: [],
    targeted: true, // task creator
    sendEmail: false,
  },
  taskDeclined: {
    category: "messaging",
    icon: "task",
    title: "Task Declined",
    roles: [],
    targeted: true, // task creator
    sendEmail: false,
  },
  taskSkipped: {
    category: "messaging",
    icon: "task_done",
    title: "Task Skipped",
    roles: [],
    targeted: true, // task creator
    sendEmail: false,
  },
  taskDue: {
    category: "messaging",
    icon: "due_date",
    title: "Task Due",
    roles: [],
    targeted: true, // assigned employee ("Remind the assignee when it's due")
    sendEmail: false,
  },
  unmatchedEmail: {
    category: "messaging",
    icon: "email_fail",
    title: "Unmatched Project Email",
    roles: ["admin"],
    sendEmail: true,
  },
  unloadingDelivery: {
    category: "delivery",
    icon: "unloading",
    title: "Unloading Started",
    roles: ["delivery"],
    customer: true,
    sendEmail: true,
  },
  salesGameStarted: {
    category: "sales",
    icon: "trophy",
    title: "New Sales Game Started",
    roles: [],
    targeted: true, // participants only
    sendEmail: true,
  },
  serviceScheduled: {
    category: "service",
    icon: "service",
    title: "Service Scheduled",
    roles: ["service"],
    targeted: true, // assigned salespeople
    customer: true,
    sendEmail: true,
  },
  serviceStarted: {
    category: "service",
    icon: "service",
    title: "Service Started",
    roles: [],
    customer: true,
    sendEmail: true,
  },
  serviceComplete: {
    category: "service",
    icon: "done_all",
    title: "Service Complete",
    roles: ["service", "scheduling"],
    targeted: true, // assigned salespeople
    customer: true,
    sendEmail: true,
    detailedEmail: true,
  },
  serviceIssueReported: {
    category: "service",
    icon: "issue",
    title: "New Service Issue Reported",
    roles: ["service"],
    targeted: true, // assigned salespeople
    sendEmail: true,
  },
  serviceFollowUpNeeded: {
    category: "service",
    icon: "warning",
    title: "Service Follow-Up Needed",
    roles: ["service"],
    targeted: true, // assigned salespeople
    customer: true,
    sendEmail: true,
  },
  // An urgent issue that can actually be worked now: either it needs no
  // material, or the material it was waiting on has arrived. Goes to
  // Scheduling, who owns getting a truck there.
  serviceIssueReadyToSchedule: {
    category: "scheduling",
    icon: "issue",
    title: "Urgent Service Ready to Schedule",
    roles: ["scheduling", "service"],
    sendEmail: true,
  },
};
